/**
 * PrivacyPolicy — Pagina pubblica /privacy accessibile senza autenticazione.
 *
 * Titolare del trattamento: FDV S.r.l. (nome commerciale "Sarabi Beach").
 * Contenuti conformi a GDPR e limitati a ciò che il sistema implementa
 * realmente (verificato nel codice).
 *
 * NB: informazione consolidata a titolo di trasparenza. Per adempimenti
 * formali (registro trattamenti, DPIA, ecc.) fare riferimento al
 * Titolare del trattamento indicato in sezione 1.
 */
import React from "react";

const LAST_UPDATED = "22 febbraio 2026";
const CONTROLLER_LEGAL = "FDV S.r.l.";
const CONTROLLER_BRAND = "Sarabi Beach";
const CONTROLLER_ADDRESS = "V.le Solferino 11, 48015 Cervia (RA)";
const CONTROLLER_VAT = "02727340396";
const CONTROLLER_EMAIL = "fdv.amministrazione@gmail.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-ink-900" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="eyebrow">Sarabi Messaging</div>
          <h1 className="text-4xl mt-1">Privacy Policy</h1>
          <p className="mt-2 text-sm text-ink-500">
            Ultimo aggiornamento: {LAST_UPDATED}
          </p>
        </div>

        <section className="prose prose-slate max-w-none space-y-6">
          <p>
            La presente informativa descrive come <strong>{CONTROLLER_LEGAL}</strong>{" "}
            (società che gestisce il nome commerciale <em>{CONTROLLER_BRAND}</em>; di
            seguito "Sarabi", "noi", "il Titolare") tratta i dati personali degli utenti
            che interagiscono con il servizio <strong>Sarabi Messaging</strong>, integrato
            con la piattaforma <strong>WhatsApp Business</strong> di Meta Platforms
            Ireland Limited. L'informativa è redatta ai sensi degli articoli 13 e 14 del
            Regolamento (UE) 2016/679 ("GDPR").
          </p>

          {/* 1 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">1. Titolare del trattamento</h2>
          <p>
            Titolare del trattamento è <strong>{CONTROLLER_LEGAL}</strong>, società che
            gestisce il nome commerciale <em>{CONTROLLER_BRAND}</em>, con sede in {CONTROLLER_ADDRESS},
            {" "}P.IVA e C.F. {CONTROLLER_VAT}.
            <br />
            Email di contatto per questioni relative alla privacy:{" "}
            <a href={`mailto:${CONTROLLER_EMAIL}`} className="text-bronze-700 underline">
              {CONTROLLER_EMAIL}
            </a>.
          </p>

          {/* 2 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">2. Tipologie di dati trattati</h2>
          <p>Nell'ambito del servizio Sarabi Messaging possono essere trattati:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Dati di contatto WhatsApp</strong>: numero di telefono, nome profilo
              pubblico, foto profilo se resa pubblica dall'utente.
            </li>
            <li>
              <strong>Contenuto dei messaggi</strong>: testo, immagini, audio, documenti,
              posizioni e altri contenuti che l'utente sceglie di inviare a Sarabi
              tramite WhatsApp.
            </li>
            <li>
              <strong>Metadati di conversazione</strong>: data e ora dei messaggi,
              identificativi WhatsApp del messaggio (<code>wamid</code>), stato di consegna e lettura,
              WhatsApp Business Account ID (WABA) e phone number ID.
            </li>
            <li>
              <strong>Rubrica contatti WhatsApp del business</strong>: sincronizzata dall'app
              WhatsApp Business fisica utilizzata dallo staff Sarabi, per consentire di
              rispondere agli stessi contatti anche dalla piattaforma centralizzata
              (funzionalità WhatsApp Business App Coexistence).
            </li>
            <li>
              <strong>Cronologia messaggi pregressi</strong>: fino a 180 giorni antecedenti
              l'onboarding, se l'utente business (staff Sarabi) ha esplicitamente scelto
              di condividerla al momento del collegamento. Il limite di 180 giorni è
              tecnico ed è determinato dalla WhatsApp Business Platform di Meta.
            </li>
            <li>
              <strong>Dati tecnici di servizio</strong>: log di ricezione dei webhook Meta,
              timestamp, identificativi tecnici.
            </li>
          </ul>
          <p>
            Sarabi <strong>non richiede categorie particolari di dati</strong> (art. 9 GDPR).
            Se un utente sceglie di condividerne volontariamente in un messaggio, questi
            vengono trattati con le medesime misure di sicurezza degli altri contenuti
            ma non vengono attivamente analizzati né categorizzati.
          </p>

          {/* 3 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">3. Finalità del trattamento</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Rispondere alle richieste di informazione, prenotazione o assistenza inviate dagli utenti tramite WhatsApp.</li>
            <li>Gestire la comunicazione bidirezionale tra clienti e staff Sarabi in modo centralizzato.</li>
            <li>Mantenere lo storico delle conversazioni per continuità del servizio e per controllo qualità interno.</li>
            <li>Adempiere ad obblighi di legge (es. richieste dell'autorità giudiziaria).</li>
            <li>Diagnostica tecnica del servizio (log applicativi, monitoraggio della sincronizzazione).</li>
          </ul>

          {/* 4 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">4. Base giuridica</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Art. 6.1.b GDPR</strong> — esecuzione di misure precontrattuali o
              contrattuali richieste dall'interessato (es. una prenotazione tramite WhatsApp).
            </li>
            <li>
              <strong>Art. 6.1.f GDPR</strong> — legittimo interesse del Titolare a gestire in
              modo efficiente le comunicazioni operative con i clienti tramite un canale
              da questi liberamente scelto.
            </li>
            <li>
              <strong>Art. 6.1.a GDPR</strong> — consenso, quando applicabile, per finalità
              specifiche ulteriori (es. invio di comunicazioni promozionali via WhatsApp),
              raccolto in modo esplicito e revocabile in qualsiasi momento.
            </li>
            <li>
              <strong>Art. 6.1.c GDPR</strong> — adempimento di obblighi legali.
            </li>
          </ul>

          {/* 5 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">5. Utilizzo di WhatsApp Business Platform / Meta</h2>
          <p>
            Sarabi Messaging utilizza la <strong>WhatsApp Business Platform</strong>{" "}
            (Cloud API) di <strong>Meta Platforms Ireland Limited</strong>, con sede in
            Merrion Road, Dublin 4, D04 X2K5, Irlanda, in modalità{" "}
            <strong>Coexistence</strong>: il numero aziendale rimane utilizzabile
            sull'app WhatsApp Business fisica in dotazione allo staff e,
            contemporaneamente, i messaggi transitano via Cloud API per essere gestiti
            dalla piattaforma Sarabi.
          </p>
          <p>
            Il trattamento dei dati da parte di Meta nell'ambito della WhatsApp Business
            Platform avviene secondo i termini e le informative ufficiali di Meta,
            consultabili ai seguenti indirizzi:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <a href="https://www.whatsapp.com/legal/business-terms" target="_blank" rel="noreferrer" className="text-bronze-700 underline">
                Condizioni WhatsApp Business
              </a>
            </li>
            <li>
              <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noreferrer" className="text-bronze-700 underline">
                Privacy Policy WhatsApp
              </a>
            </li>
            <li>
              <a href="https://www.facebook.com/legal/terms/businesstools" target="_blank" rel="noreferrer" className="text-bronze-700 underline">
                Condizioni per gli strumenti aziendali Meta
              </a>
            </li>
          </ul>
          <p>
            L'esatta qualificazione di Meta ai sensi del GDPR (in particolare se agisca
            come titolare autonomo, contitolare o responsabile del trattamento, e per
            quali specifici trattamenti) è definita nei suddetti termini ufficiali di
            Meta, che prevalgono in caso di dubbio interpretativo.
          </p>
          <p>
            Sulla piattaforma Cloud API si applica una finestra tecnica di{" "}
            <strong>24 ore</strong> imposta da Meta: al di fuori di essa i messaggi in
            uscita da Sarabi verso gli utenti possono essere inviati solo tramite modelli
            approvati da Meta, secondo le regole della piattaforma.
          </p>

          {/* 6 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">6. Fornitori / terze parti</h2>
          <p>
            Nel funzionamento tecnico del servizio Sarabi utilizza i seguenti soggetti:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Meta Platforms Ireland Limited</strong> — WhatsApp Business Platform
              (Cloud API), infrastruttura di messaggistica.
            </li>
            <li>
              <strong>Emergent Labs</strong> — provider infrastrutturale che ospita
              l'applicazione Sarabi (hosting e database gestito).
            </li>
            <li>
              <strong>Google Fonts</strong> (Google Ireland Limited) — servizio di web
              font caricato dalle pagine dell'applicazione.
            </li>
            <li>
              <strong>PostHog</strong> — strumento di analisi tecnica di utilizzo
              dell'interfaccia dell'applicazione Sarabi. Non è applicato al contenuto
              delle conversazioni WhatsApp.
            </li>
          </ul>
          <p>
            Alcuni dei fornitori sopra indicati possono trattare dati anche al di fuori
            dello Spazio Economico Europeo. In tali casi si applicano le garanzie
            previste dai rispettivi termini contrattuali ufficiali del fornitore, ai
            sensi del Capo V del GDPR.
          </p>

          {/* 7 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">7. Conservazione dei dati</h2>
          <p>
            I dati personali sono conservati per il tempo strettamente necessario alle
            finalità per cui sono stati raccolti e comunque nel rispetto degli obblighi
            di legge applicabili. Al termine dei periodi di conservazione i dati vengono
            cancellati o resi anonimi.
          </p>
          <p>
            Per informazioni specifiche sui periodi di conservazione applicati a una
            categoria di dati che La riguarda è possibile scrivere all'indirizzo email
            indicato in sezione 1.
          </p>

          {/* 8 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">8. Diritti degli interessati</h2>
          <p>
            L'interessato può in qualsiasi momento esercitare i diritti previsti dagli
            articoli 15-22 del GDPR, tra cui:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>diritto di accesso ai propri dati personali;</li>
            <li>diritto di rettifica di dati inesatti o incompleti;</li>
            <li>diritto alla cancellazione (diritto all'oblio);</li>
            <li>diritto di limitazione del trattamento;</li>
            <li>diritto alla portabilità dei dati in formato strutturato;</li>
            <li>diritto di opposizione al trattamento fondato sul legittimo interesse;</li>
            <li>diritto di revocare in qualsiasi momento un consenso precedentemente prestato, senza pregiudicare la liceità del trattamento pregresso;</li>
            <li>
              diritto di proporre reclamo all'Autorità Garante per la protezione dei
              dati personali (
              <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-bronze-700 underline">
                garanteprivacy.it
              </a>).
            </li>
          </ul>
          <p>
            Per esercitare i propri diritti è possibile inviare una richiesta a{" "}
            <a href={`mailto:${CONTROLLER_EMAIL}`} className="text-bronze-700 underline">
              {CONTROLLER_EMAIL}
            </a>. Qualora sussistano ragionevoli dubbi circa l'identità del richiedente,{" "}
            {CONTROLLER_LEGAL} potrà richiedere ulteriori informazioni necessarie per
            verificarne l'identità.
          </p>

          {/* 9 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">9. Sicurezza dei dati</h2>
          <p>
            Adottiamo misure tecniche e organizzative appropriate al livello di rischio.
            In particolare, per la componente Sarabi Messaging:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>cifratura at-rest dei token di accesso Meta mediante Fernet;</li>
            <li>verifica crittografica delle firme dei webhook in ingresso (HMAC-SHA256, header <code>X-Hub-Signature-256</code>);</li>
            <li>controllo degli accessi al backoffice basato sui ruoli (role-based access control);</li>
            <li>registro degli eventi amministrativi rilevanti (audit log);</li>
            <li>redazione automatica dei secret nei log applicativi.</li>
          </ul>

          {/* 10 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">10. Modifiche a questa informativa</h2>
          <p>
            La presente informativa può essere aggiornata per riflettere modifiche
            legislative, tecnologiche o organizzative. La versione corrente è sempre
            disponibile su questa pagina, con indicazione della data di ultimo
            aggiornamento.
          </p>

          {/* 11 ─────────────────────────────────────────────────────── */}
          <h2 className="text-2xl mt-8">11. Contatti</h2>
          <p>
            Per qualunque domanda o richiesta riguardo al trattamento dei propri dati
            personali:{" "}
            <a href={`mailto:${CONTROLLER_EMAIL}`} className="text-bronze-700 underline">
              {CONTROLLER_EMAIL}
            </a>
          </p>
        </section>

        <footer className="mt-16 pt-6 border-t border-ink-100 text-xs text-ink-500">
          © {new Date().getFullYear()} {CONTROLLER_LEGAL} — {CONTROLLER_BRAND}. Tutti i diritti riservati.
        </footer>
      </div>
    </div>
  );
}
